import React, { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../../supabaseClient';
import { useAdmin } from '../../../context/AdminContext';
import CorrectGameModal from '../modals/CorrectGameModal';

// Parse "YYYY-MM-DDTHH:mm:ss" without UTC shift
const parseLocalDate = (str) => {
    const [date, time = '00:00:00'] = (str || '').split('T');
    const [y, m, d]                 = date.split('-').map(Number);
    const [hh, mm]                  = time.split(':').map(Number);
    return new Date(y, m - 1, d, hh, mm);
};

const groupByDate = (games) => {
    const map = {};
    games.forEach(game => {
        const key = parseLocalDate(game.game_date).toLocaleDateString('en-US', {
            weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
        });
        if (!map[key]) map[key] = [];
        map[key].push(game);
    });
    return Object.keys(map)
        .sort((a, b) => new Date(b) - new Date(a))       // descending for results
        .map(dateKey => ({ dateKey, games: map[dateKey] }));
};

const GameResultsPanel = () => {
    const { activeSeason, loadData } = useAdmin();

    const [finishedGames, setFinishedGames] = useState([]);
    const [grouped,       setGrouped]       = useState([]);
    const [loading,       setLoading]       = useState(false);
    const [expandedDates, setExpandedDates] = useState({});
    const [correctingGame, setCorrectingGame] = useState(null);

    const loadFinishedGames = useCallback(async () => {
        if (!activeSeason) return;
        setLoading(true);
        const { data, error } = await supabase
            .from('games')
            .select(`
                id, game_date, game_type, status,
                team1_score, team2_score,
                team1:teams!games_team1_id_fkey!inner(id, name, logo_url),
                team2:teams!games_team2_id_fkey!inner(id, name, logo_url)
            `)
            .eq('season_id', activeSeason.id)
            .eq('status', 'finished')
            .is('team1.archived_at', null)
            .is('team2.archived_at', null)
            .order('game_date', { ascending: false });

        if (!error) setFinishedGames(data || []);
        setLoading(false);
    }, [activeSeason]);

    useEffect(() => { loadFinishedGames(); }, [loadFinishedGames]);

    useEffect(() => {
        setGrouped(groupByDate(finishedGames));
    }, [finishedGames]);

    const toggleDate = (dateKey) =>
        setExpandedDates(prev => ({ ...prev, [dateKey]: !prev[dateKey] }));

    const handleSaved = async () => {
        await Promise.all([loadFinishedGames(), loadData()]);
    };

    if (loading) return (
        <p className="text-gray-500 dark:text-gray-400 text-center py-8">Loading games…</p>
    );

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Game Results</h2>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                    {finishedGames.length} finished game{finishedGames.length !== 1 ? 's' : ''}
                </span>
            </div>

            {finishedGames.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">No finished games yet.</p>
            ) : (
                <div className="space-y-3">
                    {grouped.map(({ dateKey, games }) => (
                        <div key={dateKey} className="mb-6 last:mb-0">
                            {/* Collapsible date header */}
                            <button
                                onClick={() => toggleDate(dateKey)}
                                className="flex items-center justify-between w-full p-3 mb-2
                                           bg-gray-50 dark:bg-gray-800/40 rounded-xl border border-transparent
                                           hover:border-orange-500/30 transition-all"
                            >
                                <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    {dateKey}
                                </h3>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                                        {games.length}
                                    </span>
                                    <span className={`text-gray-400 transition-transform duration-200 ${expandedDates[dateKey] ? 'rotate-180' : ''}`}>
                                        ▼
                                    </span>
                                </div>
                            </button>

                            {expandedDates[dateKey] && (
                                <div className="space-y-3">
                                    {games.map(game => (
                                        <div key={game.id}
                                            className="border dark:border-gray-600 rounded-xl p-4 bg-white dark:bg-gray-800
                                                       flex flex-col sm:flex-row sm:items-center gap-4"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-3 flex-wrap">
                                                    {/* Team 1 */}
                                                    <div className="flex items-center gap-1.5 min-w-0">
                                                        {game.team1.logo_url
                                                            ? <img src={game.team1.logo_url} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
                                                            : <span className="shrink-0 text-sm">🏀</span>}
                                                        <span className="font-bold text-gray-900 dark:text-white truncate">{game.team1.name}</span>
                                                    </div>

                                                    <span className="font-black text-lg tabular-nums text-orange-500 dark:text-orange-400 shrink-0">
                                                        {game.team1_score} – {game.team2_score}
                                                    </span>

                                                    {/* Team 2 */}
                                                    <div className="flex items-center gap-1.5 min-w-0">
                                                        {game.team2.logo_url
                                                            ? <img src={game.team2.logo_url} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
                                                            : <span className="shrink-0 text-sm">🏀</span>}
                                                        <span className="font-bold text-gray-900 dark:text-white truncate">{game.team2.name}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <button
                                                onClick={() => setCorrectingGame(game)}
                                                className="shrink-0 px-4 py-2 text-sm font-semibold border-2 border-orange-500
                                                           text-orange-600 dark:text-orange-400 rounded-xl
                                                           hover:bg-orange-50 dark:hover:bg-orange-900/20 active:scale-95 transition-all"
                                            >
                                                ✏️ Correct
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {correctingGame && (
                <CorrectGameModal
                    game={correctingGame}
                    onClose={() => setCorrectingGame(null)}
                    onSaved={handleSaved}
                />
            )}
        </div>
    );
};

export default GameResultsPanel;